// Diálogo de confirmação com um texto (razão, comentário ou nota) — obrigatório ou opcional — e campos extras opcionais
// (`children`), usado pelas ações de autorização: revogar atribuição, segunda chave, revisar break-glass, decidir
// recertificação, encerrar regra de SoD. O erro da mutação aparece inline (ProblemAlert); o fechamento limpa o estado.
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Textarea,
} from '@giyus/ui';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ProblemAlert } from '../../../components';
import { Field } from './Fields';

export interface TextDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly title: string;
  readonly description: string;
  readonly textLabel: string;
  /** Texto obrigatório (razão) ou opcional (comentário/nota). */
  readonly textRequired?: boolean | undefined;
  readonly confirmLabel?: string | undefined;
  readonly pending?: boolean | undefined;
  readonly error?: unknown;
  /** Bloqueia o envio até os campos extras estarem válidos. */
  readonly extraValid?: boolean | undefined;
  readonly children?: ReactNode;
  readonly onConfirm: (text: string | null) => void | Promise<void>;
  readonly testId?: string | undefined;
}

export function TextDialog({
  open,
  onOpenChange,
  title,
  description,
  textLabel,
  textRequired = true,
  confirmLabel,
  pending = false,
  error,
  extraValid = true,
  children,
  onConfirm,
  testId = 'authz-text-dialog',
}: TextDialogProps) {
  const { t } = useTranslation(['authz', 'common']);
  const [text, setText] = useState('');
  const [touched, setTouched] = useState(false);
  const textId = `${testId}-text`;
  const missing = textRequired && text.trim() === '';

  const reset = (): void => {
    setText('');
    setTouched(false);
  };

  const submit = (): void => {
    setTouched(true);
    if (missing || !extraValid) {
      return;
    }
    const value = text.trim();
    void onConfirm(value === '' ? null : value);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          reset();
        }
        onOpenChange(next);
      }}
    >
      <DialogContent closeLabel={t('common:a11y.closeDialog')} data-testid={testId}>
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
        >
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>{description}</DialogDescription>
          </DialogHeader>
          {children}
          <Field
            id={textId}
            label={textLabel}
            error={touched && missing ? t('authz:dialog.textRequired') : undefined}
          >
            <Textarea
              id={textId}
              value={text}
              maxLength={1000}
              rows={3}
              aria-invalid={touched && missing}
              onChange={(event) => {
                setText(event.target.value);
              }}
            />
          </Field>
          {error !== undefined && error !== null && <ProblemAlert error={error} />}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                reset();
                onOpenChange(false);
              }}
            >
              {t('common:actions.cancel')}
            </Button>
            <Button type="submit" disabled={pending} data-testid={`${testId}-confirm`}>
              {confirmLabel ?? t('common:actions.confirm')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
